import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { SEGMENTS } from "@/lib/segments";
import { Search, ChevronLeft, ChevronRight, GitCompareArrows, X, LayoutGrid, Rows3 } from "lucide-react";
import CommentsDialog from "@/components/dashboard/CommentsDialog";
import TimelineDialog from "@/components/dashboard/TimelineDialog";
import ProfileDetailDialog from "@/components/dashboard/ProfileDetailDialog";
import CompareDialog from "@/components/dashboard/CompareDialog";
import ProfileCard from "@/components/dashboard/ProfileCard";
import ProfileRow from "@/components/dashboard/ProfileRow";
import SavedFilters from "@/components/dashboard/SavedFilters";

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1603239699774-885100e49753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/flagged/photo-1574243961314-cb2e12cc4c1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1676195470090-7c90bf539b3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
];

export default function ProfilesTable({ sources, category = "", onlyNewDefault = false, initialSource = "", defaultLayout = "table", initialCriteria = null }) {
  const ic = initialCriteria || {};
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [layout, setLayout] = useState(defaultLayout);
  const [search, setSearch] = useState(ic.search || "");
  const [source, setSource] = useState(ic.source || initialSource);
  const [cat, setCat] = useState(ic.category || category);
  const [cityFilter, setCityFilter] = useState(ic.city || "");
  const [cities, setCities] = useState([]);
  const [onlyNew, setOnlyNew] = useState(ic.only_new ?? onlyNewDefault);
  const [statusFilter, setStatusFilter] = useState(ic.status || "");
  const [sortBy, setSortBy] = useState(ic.sort_by || "first_seen");
  const [sortDir, setSortDir] = useState(ic.sort_dir || "desc");
  const [loading, setLoading] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [timelineProfile, setTimelineProfile] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const pageSize = 30;

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 8 ? s : [...s, id])));

  // keep the table's category in sync with the global header toggle, using the
  // React-recommended "adjust state during render" pattern (no effect, so it
  // also avoids the set-state-in-effect lint rule).
  const [prevCategory, setPrevCategory] = useState(category);
  if (category !== prevCategory) {
    setPrevCategory(category);
    setCat(category);
    setPage(1);
  }

  // React to programmatic filter changes coming from clickable cards / site rows
  // on the overview (same "adjust state during render" pattern).
  const [prevInitSource, setPrevInitSource] = useState(initialSource);
  if (initialSource !== prevInitSource) {
    setPrevInitSource(initialSource);
    setSource(initialSource);
    setPage(1);
  }
  const [prevOnlyNewDefault, setPrevOnlyNewDefault] = useState(onlyNewDefault);
  if (onlyNewDefault !== prevOnlyNewDefault) {
    setPrevOnlyNewDefault(onlyNewDefault);
    setOnlyNew(onlyNewDefault);
    setPage(1);
  }

  // load the list of regions/cities the user can see (for the region filter)
  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  // Next-page prefetch: after the current page renders, quietly fetch page+1
  // and warm its thumbnails so paging forward feels instant. The prefetched
  // response is reused (no second request) when the user actually pages.
  const prefetch = useRef({ key: null, data: null });
  const baseParams = { search, source, category: cat, city: cityFilter, only_new: onlyNew, status: statusFilter, sort_by: sortBy, sort_dir: sortDir, page_size: pageSize };
  const baseKey = JSON.stringify(baseParams);

  const load = useCallback(async () => {
    const key = `${baseKey}|${page}`;
    if (prefetch.current.key === key && prefetch.current.data) {
      setItems(prefetch.current.data.items);
      setTotal(prefetch.current.data.total);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/profiles", { params: { ...baseParams, page } });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (loading || !items.length || page * pageSize >= total) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/profiles", { params: { ...baseParams, page: page + 1 } });
        if (cancelled) return;
        prefetch.current = { key: `${baseKey}|${page + 1}`, data };
        const w = layout === "gallery" ? 480 : 160;
        data.items.slice(0, pageSize).forEach((p) => {
          const src = imgProxy(p.photo_url, w);
          if (!src) return;
          const im = new Image();
          im.decoding = "async";
          im.src = src;
        });
      } catch (e) { /* prefetch is best-effort */ }
    }, 900);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading, page, total, baseKey, layout]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  const setSort = (by, dir) => { setSortBy(by); setSortDir(dir); setPage(1); };
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir(field === "name" || field === "city" || field === "source" ? "asc" : "desc");
    }
    setPage(1);
  };
  const sortIndicator = (field) =>
    sortBy === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const sourceColor = (key) => (sources.find((s) => s.key === key)?.color) || "#c8aa6e";

  const currentCriteria = { search, source, city: cityFilter, category: cat,
    only_new: onlyNew, sort_by: sortBy, sort_dir: sortDir };
  const applyCriteria = (c = {}) => {
    setSearch(c.search || "");
    setSource(c.source || "");
    setCat(c.category || "");
    setCityFilter(c.city || "");
    setOnlyNew(!!c.only_new);
    setSortBy(c.sort_by || "first_seen");
    setSortDir(c.sort_dir || "desc");
    setPage(1);
  };

  return (
    <div className="bg-[#18181b] border border-[#27272a]" data-testid="profiles-table">
      {/* Filters */}
      <div className="p-4 border-b border-[#27272a] flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#71717a] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="profiles-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Pesquisar nome, número ou cidade…"
            className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none pl-9 pr-3 py-2 text-sm"
          />
        </div>
        {/* Layout toggle: Gallery / Table */}
        <div className="flex border border-[#3f3f46]" data-testid="profiles-layout-toggle">
          <button
            type="button"
            data-testid="layout-gallery"
            onClick={() => setLayout("gallery")}
            title="Vista de galeria"
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${layout === "gallery" ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}
          >
            <LayoutGrid className="w-4 h-4" /> <span className="hidden sm:inline">Galeria</span>
          </button>
          <button
            type="button"
            data-testid="layout-table"
            onClick={() => setLayout("table")}
            title="Vista de tabela"
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${layout === "table" ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}
          >
            <Rows3 className="w-4 h-4" /> <span className="hidden sm:inline">Tabela</span>
          </button>
        </div>
        <select
          data-testid="profiles-source-filter"
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="">Todos os sites</option>
          {sources.map((s) => (
            <option key={s.key} value={s.key}>{s.name}</option>
          ))}
        </select>
        <select
          data-testid="profiles-category-filter"
          value={cat}
          onChange={(e) => { setCat(e.target.value); setPage(1); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="">Todas as categorias</option>
          {SEGMENTS.map((sg) => <option key={sg.k} value={sg.k}>{sg.label}</option>)}
        </select>
        <select
          data-testid="profiles-region-filter"
          value={cityFilter}
          onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="">Todas as regiões</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          data-testid="profiles-sort"
          value={`${sortBy}:${sortDir}`}
          onChange={(e) => { const [b, d] = e.target.value.split(":"); setSort(b, d); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="first_seen:desc">Mais recentes</option>
          <option value="last_seen:desc">Última atualização</option>
          <option value="name:asc">Nome (A-Z)</option>
          <option value="duplicates:desc">Mais anúncios repetidos</option>
          <option value="photo_dup:desc">Mais fotos repetidas</option>
        </select>
        <button
          data-testid="profiles-onlynew"
          onClick={() => { setOnlyNew((v) => !v); setPage(1); }}
          className={`px-3 py-2 text-sm border transition-colors ${
            onlyNew ? "border-[#c8aa6e] text-[#c8aa6e] bg-[#c8aa6e]/10" : "border-[#3f3f46] text-[#a1a1aa]"
          }`}
        >
          Só novos (7d)
        </button>
        <select
          data-testid="profiles-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="">Ativos e desativados</option>
          <option value="ativo">Só ativos</option>
          <option value="inativo">Só desativados</option>
        </select>
        <SavedFilters currentCriteria={currentCriteria} onApply={applyCriteria} />
      </div>

      {/* Color legend — clickable site chips (ties colors to filtering) */}
      {sources.length > 0 && (
        <div className="px-4 py-2.5 border-b border-[#27272a] flex flex-wrap items-center gap-2" data-testid="source-legend">
          <span className="label-tiny mr-1">Sites:</span>
          <button
            type="button"
            data-testid="legend-all"
            onClick={() => { setSource(""); setPage(1); }}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border transition-colors ${source === "" ? "border-[#c8aa6e] text-[#fafafa] bg-[#c8aa6e]/10" : "border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]"}`}
          >
            Todos
          </button>
          {sources.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid={`legend-${s.key}`}
              onClick={() => { setSource(source === s.key ? "" : s.key); setPage(1); }}
              title={`Filtrar por ${s.name}`}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border transition-colors ${source === s.key ? "text-[#fafafa]" : "border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]"}`}
              style={source === s.key ? { borderColor: s.color, background: s.color + "22" } : undefined}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Compare toolbar */}
      {selected.length > 0 && (
        <div className="px-4 py-2.5 bg-[#c8aa6e]/10 border-b border-[#c8aa6e]/30 flex items-center justify-between text-sm" data-testid="compare-toolbar">
          <span className="text-[#e4e4e7]">
            {selected.length} selecionado{selected.length > 1 ? "s" : ""} para comparar
            <span className="text-[#71717a]"> (máx. 8)</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="compare-clear"
              onClick={() => setSelected([])}
              className="inline-flex items-center gap-1 text-[#a1a1aa] hover:text-[#fafafa] px-2 py-1"
            >
              <X className="w-3.5 h-3.5" /> Limpar
            </button>
            <button
              data-testid="compare-open"
              disabled={selected.length < 2}
              onClick={() => setCompareOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c8aa6e] text-[#09090b] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#d4b982]"
            >
              <GitCompareArrows className="w-4 h-4" /> Comparar
            </button>
          </div>
        </div>
      )}

      {/* Gallery */}
      {layout === "gallery" && (
        <div className="p-4">
          {loading && items.length === 0 ? (
            <div className="py-16 text-center text-[#71717a] text-sm">A carregar perfis…</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-[#71717a] text-sm">Nenhum perfil encontrado.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="profiles-gallery">
              {items.map((p, i) => (
                <ProfileCard
                  key={p.id}
                  p={p}
                  index={i}
                  sourceColor={sourceColor(p.source)}
                  onOpen={setDetailId}
                  fullDescription
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {layout === "table" && (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="label-tiny">
              <th className="px-3 py-3 font-medium w-8"></th>
              <th className="px-4 py-3 font-medium">Foto</th>
              <th onClick={() => toggleSort("name")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-name">Nome{sortIndicator("name")}</th>
              <th onClick={() => toggleSort("city")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-city">Cidade{sortIndicator("city")}</th>
              <th onClick={() => toggleSort("source")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-source">Site{sortIndicator("source")}</th>
              <th onClick={() => toggleSort("duplicates")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-phone" title="Ordenar por nº de anúncios repetidos">Telefone{sortIndicator("duplicates")}</th>
              <th onClick={() => toggleSort("first_seen")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-first">Primeiro Registo{sortIndicator("first_seen")}</th>
              <th onClick={() => toggleSort("last_seen")} className="px-4 py-3 font-medium cursor-pointer select-none hover:text-[#c8aa6e]" data-testid="sort-last">Última Atual.{sortIndicator("last_seen")}</th>
              <th className="px-4 py-3 font-medium">Notas</th>
              <th className="px-4 py-3 font-medium">Histórico</th>
              <th className="px-4 py-3 font-medium">Link</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <ProfileRow
                key={p.id}
                p={p}
                i={i}
                isSelected={selected.includes(p.id)}
                sourceColor={p.source_color || sourceColor(p.source)}
                placeholder={PLACEHOLDERS[i % 4]}
                onToggleSelect={toggleSelect}
                onOpenDetail={setDetailId}
                onPhoneGroup={(phone) => { setSearch(phone); setPage(1); }}
                onNotes={setActiveProfile}
                onTimeline={setTimelineProfile}
              />
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center text-[#71717a] text-sm">
                  Nenhum perfil encontrado. Clique em «Atualizar Agora» para recolher dados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      <div className="p-4 border-t border-[#27272a] flex items-center justify-between text-sm text-[#a1a1aa]">
        <span>{total} perfis</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="page-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono text-xs">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="page-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <CommentsDialog
        profile={activeProfile}
        open={!!activeProfile}
        onOpenChange={(o) => !o && setActiveProfile(null)}
        onChanged={load}
      />

      <TimelineDialog
        profile={timelineProfile}
        open={!!timelineProfile}
        onOpenChange={(o) => !o && setTimelineProfile(null)}
      />

      <ProfileDetailDialog
        profileId={detailId}
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
      />

      <CompareDialog
        ids={selected}
        open={compareOpen}
        onOpenChange={setCompareOpen}
        onOpenProfile={(id) => { setCompareOpen(false); setDetailId(id); }}
      />
    </div>
  );
}
